/**
 * File Type Registry
 * Handles file type detection, validation, and categorization
 */

import { FILE_LOADER } from '../../core/app-constants';

export class FileTypeRegistry {
  private supportedTextTypes: readonly string[];
  private supportedBinaryTypes: readonly string[];
  private binaryExtensions: readonly string[];
  private maxFileSize: number;

  constructor() {
    this.supportedTextTypes = FILE_LOADER.SUPPORTED_TEXT_TYPES;
    this.supportedBinaryTypes = FILE_LOADER.SUPPORTED_BINARY_TYPES;
    this.binaryExtensions = FILE_LOADER.BINARY_EXTENSIONS;
    this.maxFileSize = FILE_LOADER.MAX_FILE_SIZE_BYTES;
  }

  /**
   * Get all supported file types (text + binary)
   */
  getSupportedTypes(): string[] {
    return [...this.supportedTextTypes, ...this.supportedBinaryTypes];
  }

  /**
   * Get supported binary file types
   */
  getBinaryTypes(): string[] {
    return [...this.supportedBinaryTypes];
  }

  /**
   * Get maximum allowed file size in bytes
   */
  getMaxFileSize(): number {
    return this.maxFileSize;
  }

  /**
   * Check if a file is a binary format
   * @param file - File to check
   * @returns True if file is binary format
   */
  isBinaryFile(file: File): boolean {
    if (this.supportedBinaryTypes.includes(file.type)) {
      return true;
    }

    // Check file extension as fallback
    const lowerName = file.name.toLowerCase();
    return this.binaryExtensions.some((ext) => lowerName.endsWith(ext));
  }

  /**
   * Check if a file type is supported
   * @param file - File to check
   * @returns True if file type is supported
   */
  isSupportedType(file: File): boolean {
    const supportedTypes = this.getSupportedTypes();

    if (supportedTypes.includes(file.type)) {
      return true;
    }

    // Check by extension for files with empty or generic MIME types
    const lowerName = file.name.toLowerCase();

    // Check binary extensions
    if (this.binaryExtensions.some((ext) => lowerName.endsWith(ext))) {
      return true;
    }

    // Check text file extensions
    const textExtensions = ['.txt', '.text', '.md', '.markdown'];
    return textExtensions.some((ext) => lowerName.endsWith(ext));
  }

  /**
   * Validate file size
   * @param file - File to validate
   * @throws Error if file size exceeds maximum
   */
  validateFileSize(file: File): void {
    if (file.size > this.maxFileSize) {
      const maxSizeMB = this.maxFileSize / (1024 * 1024);
      throw new Error(
        `File size exceeds maximum allowed size of ${maxSizeMB}MB`
      );
    }
  }

  /**
   * Validate file type
   * @param file - File to validate
   * @throws Error if file type is not supported
   */
  validateFileType(file: File): void {
    if (!this.isSupportedType(file)) {
      throw new Error(
        `Unsupported file type: ${file.type || 'unknown'}. File: ${file.name}`
      );
    }
  }

  /**
   * Validate file (size and type)
   * @param file - File to validate
   * @throws Error if file is invalid
   */
  validateFile(file: File): void {
    this.validateFileSize(file);
    this.validateFileType(file);
  }
}
